
import { isSessionRejected } from '../api/sessionFailure';
import React, { useReducer, useEffect, useCallback, useRef } from 'react';
import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AuthContext } from './AuthContext';
import { authService } from '../api/authService';
import { refreshAccessToken } from '../api/tokenRefresh';
import { authEvents } from '../events';
import { butterToast } from '@butter/ui/patterns';
import {
  setTokens,
  getAccessToken,
  clearAllTokens,
  getAccessTokenExpiry,
  getRefreshToken,
} from '../storage/tokenStorage';
import type {
  AuthState,
  User,
  RegisterRequest,
} from '../types';

/**
 * Auth actions for reducer
 */
type AuthAction =
  | { type: 'INIT_START' }
  | { type: 'INIT_SUCCESS'; user: User }
  | { type: 'INIT_FAILED' }
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; user: User }
  | { type: 'LOGIN_FAILED'; error: Error }
  | { type: 'REFRESH_SUCCESS' }
  | { type: 'REFRESH_FAILED'; error: Error }
  | { type: 'LOGOUT' }
  | { type: 'ERROR'; error: Error };

/**
 * Auth state reducer (state machine)
 * 
 * State transitions:
 * INITIALIZING → AUTHENTICATED | UNAUTHENTICATED
 * AUTHENTICATED → AUTHENTICATED (silent refresh) | UNAUTHENTICATED
 * UNAUTHENTICATED → INITIALIZING (on login)
 */
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'INIT_START':
      return { ...state, status: 'INITIALIZING', error: null };

    case 'INIT_SUCCESS':
      return {
        status: 'AUTHENTICATED',
        user: action.user,
        tokens: {
          accessToken: getAccessToken()!,
          accessTokenExpiry: getAccessTokenExpiry()!,
          refreshToken: '', // Not exposed in state
          refreshTokenExpiry: 0,
        },
        error: null,
      };

    case 'INIT_FAILED':
      return {
        status: 'UNAUTHENTICATED',
        user: null,
        tokens: null,
        error: null,
      };

    case 'LOGIN_START':
      return { ...state, status: 'INITIALIZING', error: null };

    case 'LOGIN_SUCCESS':
      return {
        status: 'AUTHENTICATED',
        user: action.user,
        tokens: {
          accessToken: getAccessToken()!,
          accessTokenExpiry: getAccessTokenExpiry()!,
          refreshToken: '', // Not exposed in state
          refreshTokenExpiry: 0,
        },
        error: null,
      };

    case 'LOGIN_FAILED':
      return {
        status: 'UNAUTHENTICATED',
        user: null,
        tokens: null,
        error: action.error,
      };

    case 'REFRESH_SUCCESS':
      return {
        ...state,
        status: 'AUTHENTICATED',
        tokens: {
          accessToken: getAccessToken()!,
          accessTokenExpiry: getAccessTokenExpiry()!,
          refreshToken: '', // Not exposed in state
          refreshTokenExpiry: 0,
        },
        error: null,
      };

    case 'REFRESH_FAILED':
      return {
        status: 'UNAUTHENTICATED',
        user: null,
        tokens: null,
        error: action.error,
      };

    case 'LOGOUT':
      return {
        status: 'UNAUTHENTICATED',
        user: null,
        tokens: null,
        error: null,
      };

    case 'ERROR':
      return {
        ...state,
        status: 'ERROR',
        error: action.error,
      };

    default:
      return state;
  }
};

/**
 * Initial auth state
 */
const initialState: AuthState = {
  status: 'INITIALIZING',
  user: null,
  tokens: null,
  error: null,
};

export interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Auth Provider
 * 
 * Manages authentication state using state machine pattern.
 * 
 * Features:
 * - Automatic session restoration on app load
 * - Proactive token refresh (via useTokenRefreshWatcher)
 * - Request queue during refresh
 * - Sign-out only when the server rejects the refresh token (silent background refresh)
 * 
 * @example
 * ```tsx
 * import { AuthProvider } from '~/services/auth';
 * 
 * function App() {
 *   return (
 *     <AuthProvider>
 *       <YourApp />
 *     </AuthProvider>
 *   );
 * }
 * ```
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const queryClient = useQueryClient();
  const initAttempted = useRef(false);

  /**
   * Initialize auth state from stored refresh token
   * 
   * On app load:
   * 1. Check if refresh token exists in storage
   * 2. Try to refresh to get new access token
   * 3. Get user profile
   * 4. Set authenticated state
   */
  const initialize = useCallback(async () => {
    if (initAttempted.current) return;
    initAttempted.current = true;

    dispatch({ type: 'INIT_START' });

    try {
      // Check if we have a refresh token
      const refreshToken = await getRefreshToken();
      
      if (!refreshToken) {
        dispatch({ type: 'INIT_FAILED' });
        return;
      }

      // Try to refresh token to get new access token
      await refreshAccessToken();
      
      // Get user profile
      const user = await authService.getProfile();
      
      dispatch({ type: 'INIT_SUCCESS', user });
      
      // Emit session restore event for auth-sensitive query invalidation
      authEvents.emit('sessionRestore', user.id);
      
    } catch (error) {
      console.error('[Auth] ❌ Initialization failed:', error);
      // Only discard the tokens when the server refused them. A page load during a
      // provider outage used to wipe a perfectly good session, forcing a fresh login
      // once the outage passed.
      if (isSessionRejected(error)) {
        await clearAllTokens();
      }
      dispatch({ type: 'INIT_FAILED' });
    }
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  /**
   * Pre-enrollment session gate: the API answered 403 ACCOUNT_SETUP_REQUIRED on some
   * request — this session's account never finished setup and can no longer
   * hold a session. Sign out locally (no logout API call: the session is
   * already refused server-side) and tell the member how to continue; signing
   * back in returns the detailed ACCOUNT_SETUP_INCOMPLETE routing that carries
   * them to /account-setup with the right params.
   */
  useEffect(() => {
    return authEvents.on('setupRequired', () => {
      void (async () => {
        await clearAllTokens();
        // Clear before LOGOUT and the event: see logout() below.
        queryClient.clear();
        dispatch({ type: 'LOGOUT' });
        authEvents.emit('logout', null);
        butterToast.info(
          'Your account setup was never finished. Sign in again and we’ll take you to the setup screen.',
          'Account setup required',
          { id: 'account-setup-required' },
        );
      })();
    });
  }, [queryClient]);

  /**
   * Login user
   */
  const login = useCallback(async (email: string, password: string) => {
    dispatch({ type: 'LOGIN_START' });

    try {
      const response = await authService.login({ email, password });
      
      // Store tokens from session object
      await setTokens({
        accessToken: response.session.access_token,
        accessExpiresIn: response.session.expires_in,
        refreshToken: response.session.refresh_token,
        refreshExpiresIn: 7 * 24 * 60 * 60, // Assume 7 days
      });
      
      dispatch({ type: 'LOGIN_SUCCESS', user: response.user });
      
      // Emit login event for auth-sensitive query invalidation
      authEvents.emit('login', response.user.id);
      
    } catch (error) {
      const authError = error instanceof Error ? error : new Error('Login failed');
      dispatch({ type: 'LOGIN_FAILED', error: authError });
      throw error;
    }
  }, []);

  /**
   * Register new user
   */
  const register = useCallback(async (userData: RegisterRequest) => {
    dispatch({ type: 'LOGIN_START' });

    try {
      const response = await authService.register(userData);
      
      // Store tokens from session object
      await setTokens({
        accessToken: response.session.access_token,
        accessExpiresIn: response.session.expires_in,
        refreshToken: response.session.refresh_token,
        refreshExpiresIn: 7 * 24 * 60 * 60, // Assume 7 days
      });
      
      dispatch({ type: 'LOGIN_SUCCESS', user: response.user });
      
      // Emit login event for auth-sensitive query invalidation
      authEvents.emit('login', response.user.id);

      // Returned so a caller acting immediately (e.g. the demo flow creating a
      // booking right after signup) doesn't race the context re-render.
      return response.user;
    } catch (error) {
      const authError = error instanceof Error ? error : new Error('Registration failed');
      dispatch({ type: 'LOGIN_FAILED', error: authError });
      throw error;
    }
  }, []);

  /**
   * Logout user
   */
  const logout = useCallback(async () => {
    try {
      // Get refresh token for logout API
      const refreshToken = await getRefreshToken();
      
      // Call logout API (best effort)
      await authService.logout(refreshToken ?? undefined);
    } catch (error) {
      console.error('[Auth] Logout API failed:', error);
    } finally {
      // Always clear local state. The cache goes first: the 'logout' event
      // invalidates auth-sensitive queries, and a still-mounted observer would
      // refetch the old member's data with no token (two 401s per sign-out).
      // With the cache already empty, there is nothing left to refetch.
      await clearAllTokens();
      queryClient.clear();
      dispatch({ type: 'LOGOUT' });
      authEvents.emit('logout', null);
    }
  }, [queryClient]);

  /**
   * Refresh access token
   */
  // A background refresh never changes status: flipping to a loading state made the
  // header read signed-out and disabled authenticated queries on every refresh. Only a
  // server rejection signs the member out — a refresh that could not reach the server
  // (a laptop waking before its network) leaves the session for the next attempt.
  const handleRefresh = useCallback(async () => {
    try {
      await refreshAccessToken();
      dispatch({ type: 'REFRESH_SUCCESS' });
    } catch (error) {
      if (isSessionRejected(error)) {
        const authError = error instanceof Error ? error : new Error('Refresh failed');
        dispatch({ type: 'REFRESH_FAILED', error: authError });
        queryClient.clear();
      }
      throw error;
    }
  }, [queryClient]);

  const value = {
    ...state,
    login,
    register,
    logout,
    refreshAccessToken: handleRefresh,
    isAuthenticated: state.status === 'AUTHENTICATED',
    isLoading: state.status === 'INITIALIZING',
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
